import { memo } from "react";
import {
  ChatCircle,
  Check,
  File,
  Lightning,
  ListBullets,
  Robot,
  Warning,
  WarningCircle,
  X,
} from "@/components/ui/icons";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { cn } from "@/lib/utils";
import { useChannelSyncEvents } from "@/lib/selectors/sync-events";
import { StringListSection } from "./sync-event-sections";
import {
  EVENT_TYPE_LABELS,
  SOURCE_LABELS,
  TEST_STATUS_LABELS,
  statusPillClass,
  testStatusPillClass,
} from "./sync-card-meta";

/**
 * The Ano Sync "Control Tower" — a right-side panel that answers "what is the
 * current state of this work?" by deterministically reducing the channel's
 * recent Sync Events (no AI). Mounted as a sibling panel in MessageAreaPanels,
 * mirroring PinnedMessagesPanel's shell.
 */
export const ControlTowerPanel = memo(function ControlTowerPanel({
  channelId,
  onClose,
}: {
  channelId: string;
  onClose: () => void;
}) {
  const { records, state } = useChannelSyncEvents(channelId);
  const latest = state.latestActive ?? state.latest;

  return (
    <div className="relative flex w-80 flex-col bg-background">
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border/95 to-transparent" />
      <div className="h-[38px] shrink-0" />
      <div className="relative flex h-12 items-center justify-between px-4">
        <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-border to-transparent" />
        <div className="flex items-center gap-2">
          <Lightning size={16} weight="Bold" className="text-info" />
          <span className="text-sm font-semibold">Control Tower</span>
          {records.length > 0 ? (
            <span className="text-xs text-muted-foreground">
              ({records.length})
            </span>
          ) : null}
        </div>
        <ToolbarIconButton
          onClick={onClose}
          className="h-8 w-8"
          aria-label="Close control tower"
        >
          <X size={14} className="text-muted-foreground" />
        </ToolbarIconButton>
      </div>

      <div className="flex-1 space-y-3 overflow-auto p-3">
        {records.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground/60">
            No Sync activity yet. Agent and tool updates posted to this channel
            show up here as a live picture of the work.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              {state.latestTestStatus ? (
                <span
                  className={cn(
                    "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium",
                    testStatusPillClass(state.latestTestStatus),
                  )}
                >
                  {TEST_STATUS_LABELS[state.latestTestStatus]}
                </span>
              ) : null}
              {state.blockers.length > 0 ? (
                <span
                  className={cn(
                    "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium",
                    statusPillClass("error"),
                  )}
                >
                  {state.blockers.length} blocker
                  {state.blockers.length === 1 ? "" : "s"}
                </span>
              ) : null}
              {state.risks.length > 0 ? (
                <span
                  className={cn(
                    "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium",
                    statusPillClass("warning"),
                  )}
                >
                  {state.risks.length} risk{state.risks.length === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>

            {latest ? (
              <div className="space-y-1">
                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Latest
                </div>
                <div className="space-y-0.5 rounded-lg bg-foreground/[0.03] p-2">
                  <div className="text-[13px] font-semibold text-foreground">
                    {latest.title}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {SOURCE_LABELS[latest.source]} ·{" "}
                    {EVENT_TYPE_LABELS[latest.eventType]} ·{" "}
                    {new Date(latest.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            ) : null}

            <StringListSection
              title="Decisions"
              icon={<Check size={11} className="text-muted-foreground" />}
              items={state.decisions}
              rowIcon={<Check size={12} className="text-info" />}
            />
            <StringListSection
              title="Open questions"
              icon={<ChatCircle size={11} className="text-muted-foreground" />}
              items={state.openQuestions}
              rowIcon={
                <ChatCircle size={12} className="text-muted-foreground" />
              }
            />
            <StringListSection
              title="Risks"
              icon={<Warning size={11} className="text-warning" />}
              items={state.risks}
              rowIcon={<Warning size={12} className="text-warning" />}
            />
            <StringListSection
              title="Blockers"
              icon={<WarningCircle size={11} className="text-destructive" />}
              items={state.blockers}
              rowIcon={<WarningCircle size={12} className="text-destructive" />}
            />
            <StringListSection
              title="Next steps"
              icon={<ListBullets size={11} className="text-muted-foreground" />}
              items={state.nextSteps}
              rowIcon={
                <ListBullets size={12} className="text-muted-foreground" />
              }
            />
            <StringListSection
              title="Changed files"
              icon={<File size={11} className="text-muted-foreground" />}
              items={state.changedFiles}
              mono
            />
            <StringListSection
              title="Agents & tools"
              icon={<Robot size={11} className="text-muted-foreground" />}
              items={state.agentsAndTools}
              rowIcon={<Robot size={12} className="text-muted-foreground" />}
            />
          </>
        )}
      </div>
    </div>
  );
});
